import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useLang } from '@/i18n';
import { authAvailable, currentUser, initAuth, login, logout, subscribe, syncLocale, type AuthUser } from './auth';
import { LADE_INTERVALL, ladeKonto, verwerfeKonto } from './plan';
import { KontoBranding } from '@/components/Partner';

interface AuthCtx {
  user: AuthUser | null;
  /** Start-Prüfung läuft noch */
  loading: boolean;
  available: boolean;
  login: (returnTo?: string) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthCtx>({ user: null, loading: true, available: authAvailable, login, logout });

/** Stellt den Konto-Zustand bereit und verarbeitet den Rücksprung von der Anmeldeseite (docs/16) */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(currentUser());
  const [loading, setLoading] = useState(authAvailable);
  const nav = useNavigate();

  useEffect(() => {
    const off = subscribe(setUser);
    initAuth()
      .then((r) => {
        setUser(r.user);
        if (r.returnTo) nav(r.returnTo, { replace: true });
      })
      .finally(() => setLoading(false));
    return off;
  }, [nav]);

  // Wechselt die Sprache, während ein Konto angemeldet ist, wandert sie einmal
  // ins Cognito-Attribut `locale` — dann kommen auch die Mails in der neuen
  // Sprache (docs/notes/mails-sprache.md). Beim ersten Lauf passiert nichts.
  const lang = useLang();
  const seen = useRef<string | null>(null);
  useEffect(() => {
    if (!user) {
      seen.current = null;
      return;
    }
    const before = seen.current;
    seen.current = lang;
    if (before && before !== lang) {
      void syncLocale(lang);
      // Auch der Sync-Server merkt sich die Sprache — Rechnung, Freischaltung und Erinnerung kommen dann in ihr
      void ladeKonto();
    }
  }, [user, lang]);

  // Konto (Profil, Stufe, Branding — docs/21): nach dem Login holen und alle
  // 15 Minuten nachsehen, damit eine Freischaltung ohne Neuanmeldung ankommt.
  // Beim Abmelden fliegt der Cache — die nächste Person am Gerät fängt bei „frei" an.
  const sub = user?.sub;
  const vorher = useRef<string | undefined>(undefined);
  useEffect(() => {
    const war = vorher.current;
    vorher.current = sub;
    if (!sub) {
      /*
       * Nur ein echtes Abmelden (vorher angemeldet, jetzt nicht) wirft den Cache
       * weg. Kommt beim Start keine Sitzung zustande — etwa weil ohne Netz ein
       * Teil der Anmeldung nicht lädt —, bleibt er liegen: Er gilt ohnehin nur
       * für das Konto, zu dem er gehört (`cacheLesen` prüft `sub`).
       */
      if (!loading && war) verwerfeKonto();
      return;
    }
    void ladeKonto();
    const iv = window.setInterval(() => document.visibilityState === 'visible' && void ladeKonto(), LADE_INTERVALL);
    return () => window.clearInterval(iv);
  }, [sub, loading]);

  const value = useMemo<AuthCtx>(() => ({ user, loading, available: authAvailable, login, logout }), [user, loading]);
  return (
    <Ctx.Provider value={value}>
      {/* Akzentfarbe des Kontos auf den Root — hier, weil der Rahmen um alle Seiten liegt */}
      <KontoBranding />
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthCtx {
  return useContext(Ctx);
}
